/**
 * Paywall copy and hero data — port of PremiumGateCard.swift (Variant extension ≈L788–931,
 * subtitle overrides ≈L356–376, PaywallCast, preview samples ≈L2047–2235).
 * Strings are the exact Swift `L("…")` / Text("…") keys; translate at render time with t().
 */
import type { PremiumGate } from '@/domain';

export type PaywallVariant = 'story' | 'premiumCases' | 'hintLimit' | 'relaunchReminder';
export type BenefitIcon = 'lockOpen' | 'sparkles' | 'lightbulb' | 'stack' | 'calendar' | 'eyeSlash' | 'messageBadge';
export interface Benefit { icon: BenefitIcon; title: string; subtitle: string }

export const VARIANT_FOR_GATE: Record<PremiumGate, PaywallVariant> = {
  storyR5: 'story', premiumCase: 'premiumCases', hintLimit: 'hintLimit', relaunchReminder: 'relaunchReminder',
};
export const GATE_FOR_VARIANT: Record<PaywallVariant, PremiumGate> = {
  story: 'storyR5', premiumCases: 'premiumCase', hintLimit: 'hintLimit', relaunchReminder: 'relaunchReminder',
};

export function isPaywallVariant(v: unknown): v is PaywallVariant {
  return v === 'story' || v === 'premiumCases' || v === 'hintLimit' || v === 'relaunchReminder';
}

export const VARIANT_TITLE: Record<PaywallVariant, string> = {
  story: 'Unlock the rest of the case and more',
  premiumCases: 'Unlock all premium cases',
  hintLimit: 'Unlock unlimited hints',
  relaunchReminder: 'The case moved on without you',
};

export const VARIANT_SUBTITLE: Record<PaywallVariant, string> = {
  story: "Unlock Noah's phone trail, Riley's side, and the ending Cindy never saw coming.",
  premiumCases: 'Get instant access to every locked case, plus new investigations every week.',
  hintLimit: 'Get unstuck without spoiling the tea. Keep the investigation moving.',
  relaunchReminder: 'New messages, hidden receipts, and the ending are still waiting.',
};

export const HERO_HEIGHT: Record<PaywallVariant, number> = { story: 154, premiumCases: 190, hintLimit: 174, relaunchReminder: 190 };

/** Per-episode subtitle overrides (PremiumGateCard.swift subtitleText). */
export function subtitleKey(variant: PaywallVariant, episodeID: string | undefined): string {
  const storyish = variant === 'story' || variant === 'relaunchReminder';
  if (storyish && episodeID === 'episode_5_last_seen_in_miami') return "Sienna's story says she left Miami. Help Avery find out what really happened.";
  if (storyish && episodeID === 'episode_4_the_vanishing_groom') return 'Ethan never made it to the wedding. Follow his trail and help Priya find him.';
  if (variant === 'story' && episodeID === 'episode_2_read_the_room_babe') return "Unlock Jake's locked note, Bri's side, and where he's really going tomorrow.";
  if (variant === 'relaunchReminder' && episodeID === 'episode_2_read_the_room_babe') return "The shrine, the streak, and tomorrow's plans are still waiting.";
  if (variant === 'story' && episodeID === 'episode_3_double_booked') return 'Unlock who purple is, why she did it, and where you want Jordan on Saturday.';
  if (variant === 'relaunchReminder' && episodeID === 'episode_3_double_booked') return 'Saturday is still coming. She still has two cakes to taste.';
  return VARIANT_SUBTITLE[variant];
}

export const VARIANT_BENEFITS: Record<PaywallVariant, Benefit[]> = {
  story: [
    { icon: 'lockOpen', title: 'Finish this case', subtitle: 'Unlock the clues after the cliffhanger.' },
    { icon: 'sparkles', title: 'Premium episodes every week', subtitle: 'New investigations as they drop.' },
    { icon: 'lightbulb', title: 'Unlimited hints', subtitle: "Get a nudge when you're stuck." },
  ],
  premiumCases: [
    { icon: 'stack', title: 'Premium cases', subtitle: 'Play every locked investigation from the case map.' },
    { icon: 'calendar', title: 'New episodes every week', subtitle: 'Fresh phone trails and twists every week.' },
    { icon: 'lightbulb', title: 'Unlimited hints', subtitle: "Get a nudge when you're stuck." },
  ],
  hintLimit: [
    { icon: 'lightbulb', title: 'Unlimited hints', subtitle: "Reveal a nudge whenever you're stuck." },
    { icon: 'eyeSlash', title: 'No-spoiler help', subtitle: 'Small pushes, not the full answer.' },
    { icon: 'stack', title: 'Premium cases included', subtitle: 'Unlock every case and weekly drops too.' },
  ],
  relaunchReminder: [
    { icon: 'messageBadge', title: 'New receipts waiting', subtitle: 'Open the texts that dropped after the cliffhanger.' },
    { icon: 'lockOpen', title: 'Finish this case', subtitle: 'Unlock the ending and the hidden phone trail.' },
    { icon: 'sparkles', title: 'Premium episodes every week', subtitle: 'Fresh cases keep landing every week.' },
  ],
};

export interface CastMember { name: string; asset: string }
export interface PaywallCast { primary: CastMember; left: CastMember; right: CastMember }

export function castForEpisode(episodeID: string | undefined): PaywallCast {
  switch (episodeID) {
    case 'episode_4_the_vanishing_groom':
      return { primary: { name: 'Priya', asset: 'ep4_portraits_priya_v2' }, left: { name: 'Arvind', asset: 'ep4_portraits_arvind_v1' }, right: { name: 'Ethan', asset: 'ep4_portraits_ethan_v2' } };
    case 'episode_5_last_seen_in_miami':
      return { primary: { name: 'Avery', asset: 'ep5_avatar_avery' }, left: { name: 'Tessa', asset: 'ep5_avatar_tessa' }, right: { name: 'Chase', asset: 'ep5_avatar_chase' } };
    case 'episode_2_read_the_room_babe':
      return { primary: { name: 'Maya', asset: 'maya-rivera-avatar' }, left: { name: 'Bri', asset: 'brianna-cole-avatar' }, right: { name: 'Jake', asset: 'jake-novak-avatar' } };
    case 'episode_3_double_booked':
      return { primary: { name: 'Jordan', asset: 'jordan-reyes-avatar' }, left: { name: 'Trey', asset: 'trey-walsh-avatar' }, right: { name: 'Lauren', asset: 'lauren-pike-avatar' } };
    default:
      return { primary: { name: 'Cindy', asset: 'cindy-parker-avatar-v2' }, left: { name: 'Riley', asset: 'riley-carter-avatar-v3' }, right: { name: 'Noah', asset: 'noah-hayes-avatar-v2' } };
  }
}

/** `localized: false` mirrors Swift literals that are not wrapped in L() (names, "Premium", "seen"…). */
export interface CasePreview { kind: 'case'; id: string; episodeCode: string; title: string; status: string; teaser: string; icon: 'lock' | 'sparkles' }
export interface MessagePreview { kind: 'message'; id: string; sender: string; context: string; contextLocalized: boolean; message: string; messageLocalized: boolean; avatar: string }
export type HeroPreview = CasePreview | MessagePreview;

export const CASE_MAP_PREVIEWS: HeroPreview[] = [
  { kind: 'case', id: 'late-library', episodeCode: 'EP 02', title: 'Late Library Nights', status: 'Premium', teaser: 'She said study session. Maps say midnight.', icon: 'lock' },
  { kind: 'message', id: 'riley-story', sender: 'Riley', context: '2m ago', contextLocalized: true, message: 'wait... you said office. why are you in her story? 😭', messageLocalized: false, avatar: 'InstagramKaylaProfile' },
  { kind: 'case', id: 'no-caller-id', episodeCode: 'EP 03', title: 'No Caller ID', status: 'Premium', teaser: 'Three missed calls. One unsaved number.', icon: 'lock' },
  { kind: 'message', id: 'morgan-deleted', sender: 'Morgan', context: 'seen', contextLocalized: false, message: "he deleted it but i saw everything. i'm shaking.", messageLocalized: true, avatar: 'InstagramMorganProfile' },
  { kind: 'case', id: 'weekly-drop', episodeCode: 'NEXT', title: 'New case every week', status: 'Fresh', teaser: 'Fresh phone trails before the tea cools.', icon: 'sparkles' },
  { kind: 'message', id: 'val-tagged-photo', sender: 'Val', context: 'now', contextLocalized: false, message: "open the tagged pic. that's literally his jacket??", messageLocalized: true, avatar: 'InstagramValProfile' },
];

export interface HintPreview { id: string; title: string; status: string; statusLocalized: boolean; teaser: string; icon: 'clock' | 'location' | 'message' }
export const HINT_PREVIEWS: HintPreview[] = [
  { id: 'timestamp', title: 'Hint 01', status: 'smart nudge', statusLocalized: true, teaser: 'check the timestamp, not the caption.', icon: 'clock' },
  { id: 'location', title: 'Hint 02', status: 'locked', statusLocalized: false, teaser: 'the story says one place. maps say another.', icon: 'location' },
  { id: 'chat-detail', title: 'Hint 03', status: 'next clue', statusLocalized: true, teaser: "riley's tiny typo changes the whole vibe.", icon: 'message' },
];

export interface RelaunchPreview { id: string; sender: string; senderLocalized: boolean; context: string; contextLocalized: boolean; message: string; avatar: string; icon: 'bell' | 'message' | 'lock' }
export function relaunchPreviews(episodeID: string | undefined): RelaunchPreview[] {
  const detectea: RelaunchPreview = { id: 'detectea-receipts', sender: 'Detectea', senderLocalized: false, context: 'case update', contextLocalized: true, message: 'new receipts dropped while you were gone.', avatar: 'SplashAppIcon', icon: 'lock' };
  if (episodeID === 'episode_2_read_the_room_babe') {
    return [
      { id: 'bri-story-posted', sender: 'Bri 🎸', senderLocalized: true, context: 'now', contextLocalized: false, message: 'posted a story. jake screenshotted it in four seconds.', avatar: 'brianna-cole-avatar', icon: 'bell' },
      { id: 'jake-explain', sender: 'Jake', senderLocalized: false, context: '3 texts', contextLocalized: true, message: "it's not what it looks like. also don't open the locked note.", avatar: 'jake-novak-avatar', icon: 'message' },
      detectea,
    ];
  }
  return [
    { id: 'riley-cindy-posted', sender: 'Riley', senderLocalized: false, context: 'now', contextLocalized: false, message: 'cindy posted again. noah is literally in the background??', avatar: 'InstagramKaylaProfile', icon: 'bell' },
    { id: 'noah-explain', sender: 'Noah', senderLocalized: false, context: '3 texts', contextLocalized: true, message: "i can explain, but please don't open riley's screenshots yet.", avatar: 'noah-hayes-avatar-v2', icon: 'message' },
    detectea,
  ];
}
